import axiosHelper from '../utils/axios';

export default (() => {
  const networkAssetPath = "networkAsset/airfinder";
  const networkAssetPathV2 = "networkAsset/airfinder/v2";
  const networkAssetPathV3 = "networkAsset/airfinder/v3";
  const networkAssetPathV4 = "networkAsset/airfinder/v4";
  const statusPath = `${networkAssetPathV2}/status`;
  const statusPathV3 = `${networkAssetPathV3}/status`;
  const statusPathV4 = `${networkAssetPathV4}/status`;
  const sitePath = 'networkAsset/airfinder/site/';

  function deriveHealthStatusCount(v4) {
    const statusList = v4?.statusList;
    if (!Array.isArray(statusList) || statusList.length === 0) return null;

    return statusList.reduce(
      (acc, row) => {
        const key = row?.health;
        if (!key) return acc;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      {}
    );
  }

  async function getNetworkConnectionStatus(siteId) {
    const response = await axiosHelper
      .getNetworkAsset()
      .get(`${statusPath}?siteId=${siteId}`);
    return response.data;
  }

  async function getDeviceStatus(siteId) {
    // Build the summary (donut charts) from the 4 v4 endpoints without extra list calls
    const types = [
      { key: 'gw', assetType: 'GATEWAY' },
      { key: 'ap', assetType: 'ACCESS_POINT' },
      { key: 'lb', assetType: 'LOCATION' },
      { key: 'tags', assetType: 'TAG' },
    ];

    const requests = types.map(t =>
      axiosHelper.getNetworkAsset().get(`${statusPathV4}/${t.key}?format=json&siteId=${siteId}`)
    );

    const results = await Promise.allSettled(requests);
    const rows = types.map((t, idx) => {
      const res = results[idx];
      const data = res && res.status === 'fulfilled' ? res.value?.data : null;
      const derivedHealthStatusCount =
        data && !data.healthStatusCount && t.assetType !== 'TAG' ? deriveHealthStatusCount(data) : null;
      return {
        assetType: t.assetType,
        label: t.assetType,
        totalCount: data?.totalCount ?? 0,
        healthStatusCount: data?.healthStatusCount ?? derivedHealthStatusCount ?? null,
        value: [],
      };
    });

    return { rows, total: rows.length };
  }

  async function getDevicesCount(siteId) {
    const url = networkAssetPathV2.concat("/count?siteId=", siteId);

    const response = await axiosHelper
      .getNetworkAsset()
      .get(url);

    return response.data;
  }

  async function getUnprovisionedLocationBeaconStatus(
    siteId,
    page,
    size,
    orderBy,
    sortBy
  ) {
    const url = networkAssetPathV3.concat(
      "/locations/unprovisioned?siteId=",
      siteId,
      "&page=",
      page.toString(),
      "&size=",
      size ? size.toString() : "",
      "&orderBy=",
      orderBy,
      "&sort=lbLastSeenTime%3Aasc"      
    );

    const r = await axiosHelper
      .getNetworkAsset()
      .get(url);

    const totalRecordsHeader = r.headers["total_records"];
    const totalRows = totalRecordsHeader ? Number(totalRecordsHeader) : 0;
    const data = r.data;

    return { rows: data, total: totalRows };
  }

  async function getUnprovisionedAccessPointStatus(
    siteId,
    page,
    size,
    orderBy,
    sortBy
  ) {
    const url = networkAssetPathV3.concat(
      "/accessPoints/unprovisioned?siteId=",
      siteId,
      "&page=",
      page.toString(),
      "&size=",
      size ? size.toString() : "",
      "&orderBy=",
      orderBy,
      "&sortBy=",
      sortBy
    );

    const r = await axiosHelper
      .getNetworkAsset()
      .get(url);

    const totalRecordsHeader = r.headers["total_records"];
    const totalRows = totalRecordsHeader ? Number(totalRecordsHeader) : 0;
    const data = r.data;

    return { rows: data, total: totalRows };
  }

  async function getGatewayStatus(
    siteId,
    page,
    size,
    orderBy,
    sortBy
  ) {
    let url = `${statusPathV4}/gw?format=json&siteId=${siteId}`;
    
    if (page) {
      url += `&page=${page}`;
    }
    if (size) {
      url += `&size=${size}`;
    }
    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }
    if (orderBy) {
      url += `&sortOrder=${orderBy}`;
    }

    const r = await axiosHelper
      .getNetworkAsset()
      .get(url);

    const v4 = r.data; // { assetType, totalCount, healthStatusCount, statusList }
    const data = (v4.statusList || []).map(row => {
      row.properties.nameSearchField = row.properties.name ? row.properties.name.toLowerCase() : '';
      row.properties.nodeSearchField = row.properties.nodeAddress ? row.properties.nodeAddress.replaceAll('-', '').toLowerCase() : '';
      return row;
    });

    return { rows: data, total: v4.totalCount || 0 };
  }
  async function getCSVData(
    siteId,
    page,
    size,
    orderBy,
    sortBy,
    format,
    call
  ) {
    let basePath = (call == '/locations/unprovisioned?' || call == '/accessPoints/unprovisioned?' || call == '/status?') ? networkAssetPathV3 : statusPathV3;
    const url = basePath.concat(
      call,
      generateParamsString(
        siteId,
        page,
        size,
        orderBy,
        sortBy,
        format
      ),
    );
    const r = await axiosHelper
      .getNetworkAsset()
      .get(url);
      return r.data;
  }

  async function getAccessPointStatus(
    siteId,
    page,
    size,
    orderBy,
    sortBy
  ) {
    let url = `${statusPathV4}/ap?format=json&siteId=${siteId}`;
    
    if (page) {
      url += `&page=${page}`;
    }
    if (size) {
      url += `&size=${size}`;
    }
    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }
    if (orderBy) {
      url += `&sortOrder=${orderBy}`;
    }

    const r = await axiosHelper
      .getNetworkAsset()
      .get(url);

    const v4 = r.data;
    const data = (v4.statusList || []).map(row => {
      row.properties.nameSearchField = row.properties.name ? row.properties.name.toLowerCase() : '';
      row.properties.nodeSearchField = row.properties.nodeAddress ? row.properties.nodeAddress.replaceAll('-', '').toLowerCase() : '';
      return row;
    });

    return { rows: data, total: v4.totalCount || 0 };
  }

  async function getLocationBeaconStatus(
    siteId,
    page,
    size,
    orderBy,
    sortBy
  ) {
    let url = `${statusPathV4}/lb?format=json&siteId=${siteId}`;
    
    if (page) {
      url += `&page=${page}`;
    }
    if (size) {
      url += `&size=${size}`;
    }
    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }
    if (orderBy) {
      url += `&sortOrder=${orderBy}`;
    }

    const r = await axiosHelper
      .getNetworkAsset()
      .get(url);

    const v4 = r.data;
    const data = (v4.statusList || []).map(row => {
      row.properties.nameSearchField = row.properties.name ? row.properties.name.toLowerCase() : '';
      row.properties.nodeSearchField = row.properties.nodeAddress ? row.properties.nodeAddress.replaceAll('-', '').toLowerCase() : '';
      return row;
    });

    return { rows: data, total: v4.totalCount || 0 };
  }

  async function getTagStatus(
    siteId,
    page,
    size,
    orderBy,
    sortBy
  ) {
    let url = `${statusPathV4}/tags?format=json&siteId=${siteId}`;
    
    if (page) {
      url += `&page=${page}`;
    }
    if (size) {
      url += `&size=${size}`;
    }
    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }
    if (orderBy) {
      url += `&sortOrder=${orderBy}`;
    }

    const r = await axiosHelper
      .getNetworkAsset()
      .get(url);

    const v4 = r.data;
    const data = (v4.statusList || []).map(row => {
      row.properties.nameSearchField = row.properties.name ? row.properties.name.toLowerCase() : '';
      row.properties.nodeSearchField = row.properties.nodeAddress ? row.properties.nodeAddress.replaceAll('-', '').toLowerCase() : '';
      return row;
    });

    return { rows: data, total: v4.totalCount || 0 };
  }

  async function getSiteInfo(siteId) {
    const url = sitePath.concat(siteId);

    const r = await axiosHelper
      .getNetworkAsset()
      .get(url);

    return r.data;
  }

  function generateParamsString(
    siteId,
    page,
    size,
    orderBy,
    sortBy,
    format = 'json'
  ) {
    let params = 'siteId='.concat(siteId);
    if (page && format != 'csv') {
      params = params.concat("&page=", page.toString());
    }
    if (size && format != 'csv') {
      params = params.concat("&size=", size.toString());
    }
    if (orderBy) {
      params = params.concat("&orderBy=", orderBy);
    }
    if (sortBy) {
      params = params.concat("&sortBy=", sortBy);
    }
    if (format) {
      params = params.concat("&format=", format);
    }
    return params;
  }

  async function restartDevice(deviceType, nodeAddress) {
    const payload = {
      nodeAddress: nodeAddress,
      nodeType: deviceType
    }
    const response = await axiosHelper.getClientEdge().post('/v2/restartCommand',payload);
    return response;
  }

  return {
    getNetworkConnectionStatus,
    getSiteInfo,
    getDeviceStatus,
    getDevicesCount,
    restartDevice,
    getUnprovisionedLocationBeaconStatus,
    getUnprovisionedAccessPointStatus,
    getGatewayStatus,
    getCSVData,
    getAccessPointStatus,
    getLocationBeaconStatus,
    getTagStatus,
  };
})();
